import { useTranslation } from "react-i18next";

import { formatConditionBadge } from "../conditionBadge";
import { getFieldLabel } from "../conditions";
import { getRuleLabel } from "../labels";
import type { FormCondition } from "../schemas";
import { ConditionParts } from "./ConditionParts";

import type { OverlapGroup, SegmentField } from "@/features/segments/types";

export type MergeSource = {
  condition: FormCondition;
  ruleIndex: number;
};

type MergeProps = {
  group: OverlapGroup;
  sources: MergeSource[];
  fields: SegmentField[];
};

export function MergeSummary({ group, sources, fields }: MergeProps) {
  const { t, i18n } = useTranslation();
  const ruleNumbers = [
    ...new Set(sources.map(({ ruleIndex }) => ruleIndex)),
  ].map((ruleIndex) => String(ruleIndex + 1));

  return (
    <span>
      {t(
        group.isNegative
          ? "admin.segments.builder.overlap.rowExcluded"
          : "admin.segments.builder.overlap.row",
        {
          count: group.members.length,
          field: getFieldLabel(fields, group.field),
          merged: group.valueChunks.length,
        },
      )}
      <span className="text-muted-foreground">
        {" · "}
        {t("admin.segments.builder.overlap.inRules", {
          count: ruleNumbers.length,
          rules: new Intl.ListFormat(i18n.language, {
            type: "conjunction",
          }).format(ruleNumbers),
        })}
      </span>
    </span>
  );
}

type PreviewConditionProps = MergeSource & {
  fields: SegmentField[];
  testId: string;
};

function PreviewCondition({
  condition,
  ruleIndex,
  fields,
  testId,
}: PreviewConditionProps) {
  const { t } = useTranslation();
  const parts = formatConditionBadge(condition, fields, t);
  if (!parts) return null;

  return (
    <li className="flex flex-col gap-0.5 text-sm" data-testid={testId}>
      <span className="text-xs text-muted-foreground">
        {getRuleLabel(t, ruleIndex)}
      </span>
      <ConditionParts parts={parts} />
    </li>
  );
}

export function MergePreview({ group, sources, fields }: MergeProps) {
  const { t } = useTranslation();
  const results = group.valueChunks.flatMap((values, index) => {
    const source = sources[index];
    return source
      ? [{ ...source, condition: { ...source.condition, values } }]
      : [];
  });

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-2">
        <h4 className="text-xs font-medium [color:var(--badge-info-text)]">
          {t("admin.segments.builder.overlap.after")}
        </h4>
        <ul className="flex flex-col gap-2 rounded-md border [border-color:var(--badge-info-border)] [background:var(--badge-info-bg)] p-3">
          {results.map((result) => (
            <PreviewCondition
              key={result.condition.clientKey}
              {...result}
              fields={fields}
              testId="overlap-result"
            />
          ))}
        </ul>
      </section>
      <section className="flex flex-col gap-2">
        <h4 className="text-xs font-medium text-muted-foreground">
          {t("admin.segments.builder.overlap.before", {
            count: sources.length,
          })}
        </h4>
        <ul className="flex flex-col gap-3 border-l pl-3">
          {sources.map((source) => (
            <PreviewCondition
              key={source.condition.clientKey}
              {...source}
              fields={fields}
              testId="overlap-source"
            />
          ))}
        </ul>
      </section>
    </div>
  );
}
